import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { listShopCategories, listShopProducts } from "@/lib/shop.functions";
import { useHideCommerce } from "@/lib/native-app";
import { Loader2 } from "lucide-react";
import { GiftMemberships } from "@/components/shop/GiftMemberships";
import { AffiliateDisclosure, CategoryIcon, ShopFilter, ShopGrid } from "@/components/shop/ShopBrowse";
import { pickTopProducts } from "@/lib/top-picks";

export { formatShopPrice as formatPrice } from "@/components/shop/ShopProductCard";

export const Route = createFileRoute("/shop/")({
  head: () => ({
    meta: [
      { title: "Shop — Calming Tools, Journals & Gifts | Recovery Trauma™" },
      {
        name: "description",
        content:
          "A curated Recovery Trauma™ wellbeing shop: calming and sensory tools, journals, books, digital recovery resources and gift memberships.",
      },
      { property: "og:title", content: "Recovery Trauma™ Shop — Small Tools. Gentle Steps." },
      {
        property: "og:description",
        content: "Calming tools, journals, books, digital resources and gift memberships, gently curated.",
      },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
    links: [{ rel: "canonical", href: "https://recoverytrauma.com/shop" }],
  }),
  component: ShopIndexPage,
});

function ShopIndexPage() {
  const fetchProducts = useServerFn(listShopProducts);
  const fetchCategories = useServerFn(listShopCategories);
  const hideCommerce = useHideCommerce();
  const { data, isLoading } = useQuery({ queryKey: ["shop", "products"], queryFn: () => fetchProducts() });
  const { data: catData } = useQuery({ queryKey: ["shop", "categories"], queryFn: () => fetchCategories() });

  const products = data?.products ?? [];
  const categories = catData?.categories ?? [];
  const featured = products.filter((p) => p.is_featured);
  const recommended = products.filter((p) => p.is_recommended && !p.is_featured);
  const topPicks = pickTopProducts(products);

  return (
    <main className="mx-auto w-full max-w-6xl px-4 py-6 sm:py-10">
      <header className="mb-8 overflow-hidden rounded-3xl border bg-gradient-to-br from-primary/10 via-card to-card px-5 py-10 text-center sm:px-10 sm:py-14">
        <p className="mb-2 text-xs font-medium uppercase tracking-[0.2em] text-[var(--gold-deep)]">Recovery Trauma™ Shop</p>
        <h1 className="mx-auto max-w-3xl font-serif text-3xl leading-tight sm:text-5xl">
          Small Tools. Gentle Steps. Meaningful Support.
        </h1>
        <p className="mx-auto mt-4 max-w-xl text-muted-foreground">
          Calming tools, journals, books and resources we’ve gently chosen — plus gift memberships for someone you care about.
        </p>
      </header>

      {hideCommerce ? (
        <p className="rounded-2xl border bg-card p-6 text-center text-muted-foreground">The shop is available on our website.</p>
      ) : isLoading ? (
        <div className="flex justify-center py-16">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" aria-label="Loading" />
        </div>
      ) : (
        <ShopFilter products={products} categories={categories}>
          {topPicks.length > 0 && (
            <section id="top-picks" aria-labelledby="top-picks-title" className="mb-12 scroll-mt-24 rounded-3xl border bg-gradient-to-br from-[var(--gold-deep)]/10 via-card to-primary/5 p-5 sm:p-8">
              <p className="text-xs font-medium uppercase tracking-[0.2em] text-[var(--gold-deep)]">From Jana, with love ✨</p>
              <h2 id="top-picks-title" className="mt-1 font-serif text-2xl sm:text-3xl">My Top {topPicks.length} Picks</h2>
              <p className="mb-5 mt-2 max-w-2xl text-sm text-muted-foreground">
                The comforting, grounding things I’d gently recommend for your own self-care corner.
              </p>
              <ShopGrid products={topPicks} />
              <Link
                to="/shop"
                hash="gift-memberships"
                className="mt-5 flex flex-col items-center gap-3 rounded-2xl border border-gold/40 bg-gradient-to-br from-gold-light/40 via-card to-card p-5 text-center transition hover:shadow-sm sm:flex-row sm:text-start"
              >
                <span className="text-3xl" aria-hidden>
                  🎁
                </span>
                <span className="flex-1">
                  <span className="block font-serif text-lg font-semibold">Bonus pick — Gift Membership Cards</span>
                  <span className="block text-sm text-muted-foreground">
                    Give someone a calm place to heal. One-time payment, never renews — 3, 6 or 12 months.
                  </span>
                </span>
                <span className="rounded-full bg-[var(--gold-deep)] px-4 py-2 text-sm font-medium text-[#fffdf6]">
                  See the gift cards
                </span>
              </Link>
            </section>
          )}
          <section aria-labelledby="cats" className="mb-12">
            <h2 id="cats" className="mb-4 text-xl font-semibold">Shop by category</h2>
            <ul className="grid grid-cols-2 gap-3 sm:grid-cols-3">
              {categories.map((c) => (
                <li key={c.slug}>
                  <Link
                    to="/shop/category/$category"
                    params={{ category: c.slug }}
                    className="flex h-full flex-col gap-2 rounded-2xl border bg-card p-4 transition hover:border-primary/50 hover:shadow-sm"
                  >
                    <span className="flex h-10 w-10 items-center justify-center rounded-full bg-primary/10">
                      <CategoryIcon name={c.icon} className="h-5 w-5 text-primary" />
                    </span>
                    <span className="text-sm font-medium sm:text-base">{c.name}</span>
                    {c.description && <span className="hidden text-xs text-muted-foreground sm:block">{c.description}</span>}
                  </Link>
                </li>
              ))}
            </ul>
          </section>

          {featured.length > 0 && (
            <section className="mb-12" aria-labelledby="featured">
              <h2 id="featured" className="mb-4 text-xl font-semibold">Featured</h2>
              <ShopGrid products={featured} />
            </section>
          )}

          <GiftMemberships />

          {recommended.length > 0 && (
            <section className="mt-12" aria-labelledby="recommended">
              <h2 id="recommended" className="mb-4 text-xl font-semibold">Recommended for you</h2>
              <ShopGrid products={recommended} />
            </section>
          )}
        </ShopFilter>
      )}

      <AffiliateDisclosure />
      <p className="mt-6 text-center text-sm text-muted-foreground">
        Received a gift? <Link to="/redeem-gift" className="font-medium text-[var(--gold-deep)] underline">Redeem Your Gift</Link>
      </p>
    </main>
  );
}
